import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { BriefBulletsNattService } from '@/services/briefBullets/briefBulletsNattService';
import { Calendar, Clock, AlertCircle, CheckCircle2, PlayCircle, Trash2, Upload, Loader2, Pause, Play, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { SupabaseStorageService } from '@/services/photo/supabaseStorageService';
import { supabase } from '@/integrations/supabase/client';

export function BriefBulletsNattManager() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [manualNtbId, setManualNtbId] = useState('');
  const [manualSearchDate, setManualSearchDate] = useState('');
  const [photoUrl, setPhotoUrl] = useState('/images/kai-logo.png');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Fetch task status
  const { data: taskStatus, isLoading: isLoadingTask } = useQuery({
    queryKey: ['brief-bullets-natt-task'],
    queryFn: async () => {
      const task = await BriefBulletsNattService.getTaskStatus();
      // Load photo URL from task config
      if (task?.config?.photo_url) {
        setPhotoUrl(task.config.photo_url);
      }
      return task;
    },
    refetchInterval: 30000,
  });

  // Fetch today's status
  const { data: todayStatus, isLoading: isLoadingToday } = useQuery({
    queryKey: ['brief-bullets-natt-today'],
    queryFn: () => BriefBulletsNattService.getTodayStatus(),
    refetchInterval: 30000,
  });

  // Fetch task logs
  const { data: taskLogs, isLoading: isLoadingLogs } = useQuery({
    queryKey: ['brief-bullets-natt-logs'],
    queryFn: () => BriefBulletsNattService.getTaskLogs(5),
    refetchInterval: 30000,
  });

  // Fetch publish history
  const { data: publishHistory, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['brief-bullets-natt-history'],
    queryFn: () => BriefBulletsNattService.getPublishHistory(7),
    refetchInterval: 60000,
  });

  // Toggle automatic publishing
  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => BriefBulletsNattService.toggleAutomaticPublishing(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brief-bullets-natt-task'] });
      toast({
        title: 'Innstillinger oppdatert',
        description: 'Automatisk publisering er oppdatert.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Feil',
        description: error instanceof Error ? error.message : 'Kunne ikke oppdatere innstillinger',
      });
    },
  });

  // Manual publish
  const publishMutation = useMutation({
    mutationFn: () => BriefBulletsNattService.triggerManualPublish(
      manualNtbId || undefined,
      manualSearchDate || undefined
    ),
    onSuccess: (result) => {
      // Check if task was skipped (e.g., task is paused)
      if (result.skipped) {
        toast({
          variant: 'destructive',
          title: 'Publisering hoppet over',
          description: result.reason || 'Oppgaven er pauset. Aktiver automatisk publisering først.',
        });
        return;
      }
      
      if (result.success && result.articleId) {
        toast({
          title: 'Publisert!',
          description: 'Artikkelen er publisert.',
        });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-natt-today'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-natt-logs'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-natt-history'] });
        setManualNtbId('');
        setManualSearchDate('');
      } else {
        toast({
          variant: 'destructive',
          title: 'Publisering feilet',
          description: result.error || 'Ukjent feil',
        });
      }
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Feil',
        description: error instanceof Error ? error.message : 'Kunne ikke publisere artikkel',
      });
    },
  });

  // Delete today's article
  const deleteMutation = useMutation({
    mutationFn: () => BriefBulletsNattService.deleteTodayArticle(),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Slettet!',
          description: 'Dagens artikkel er slettet.',
        });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-natt-today'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-natt-history'] });
      } else {
        toast({
          variant: 'destructive',
          title: 'Sletting feilet',
          description: result.error || 'Ukjent feil',
        });
      }
    },
  });

  // Handle photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPhoto(true);
    try {
      const uploadedUrl = await SupabaseStorageService.uploadImage(file, 'profile-images');
      setPhotoUrl(uploadedUrl);
      
      // Save photo URL using RPC
      const { error } = await (supabase as any).rpc('update_briefr_bullets_natt_config', {
        new_photo_url: uploadedUrl
      });

      if (error) {
        console.error('Error saving photo URL:', error);
      }

      toast({
        title: 'Bilde lastet opp',
        description: 'Profilbildet er oppdatert',
      });
      
      queryClient.invalidateQueries({ queryKey: ['brief-bullets-natt-task'] });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: 'Feil',
        description: 'Kunne ikke laste opp bilde',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const isLoading = isLoadingTask || isLoadingToday || isLoadingLogs || isLoadingHistory;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🌙 Briefr Bullets: Hva skjedde i natt?
        </CardTitle>
        <CardDescription>
          Automatisk publisering av nattens viktigste nyheter kl. 05:30:50
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Status i dag</h3>
            {todayStatus?.published ? (
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10">
                <CheckCircle2 className="h-3 w-3 text-green-600" />
                <span className="text-sm text-green-600 font-medium">Publisert</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-muted">
                <Clock className="h-3 w-3" />
                <span className="text-sm">Venter</span>
              </div>
            )}
          </div>

          {todayStatus?.article && (
            <div className="text-sm text-muted-foreground">
              Publisert kl. {format(new Date(todayStatus.article.created_at), 'HH:mm', { locale: nb })}
            </div>
          )}

          {taskStatus?.error_message && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Siste feil:</p>
                <p>{taskStatus.error_message}</p>
                {taskStatus.retry_count > 0 && (
                  <p className="mt-1 text-xs">
                    Forsøk {taskStatus.retry_count} av {taskStatus.config?.max_retries || 1}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Task Configuration */}
        {taskStatus && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Konfigurasjon</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="mt-1">
                  {taskStatus.status === 'active' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600">
                      Aktiv
                    </span>
                  ) : taskStatus.status === 'paused' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted">
                      Pauset
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                      Feilet
                    </span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Publiseringstid:</span>
                <div className="mt-1 font-medium">{taskStatus.schedule_time?.slice(0, 8)}</div>
              </div>
              {taskStatus.config?.retry_time && (
                <div>
                  <span className="text-muted-foreground">Retry-tid:</span>
                  <div className="mt-1 font-medium">{taskStatus.config.retry_time.slice(0, 5)}</div>
                </div>
              )}
              {taskStatus.next_run && (
                <div>
                  <span className="text-muted-foreground">Neste kjøring:</span>
                  <div className="mt-1 font-medium">
                    {format(new Date(taskStatus.next_run), 'dd.MM HH:mm', { locale: nb })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photo Upload */}
        <div className="space-y-2 pt-6 border-t">
          <h3 className="font-medium">Profilbilde for Kai</h3>
          <div className="flex items-center gap-3">
            <img 
              src={photoUrl} 
              alt="Kai profil" 
              className="w-16 h-16 rounded-full object-cover border-2 border-border"
            />
            <div className="flex-1">
              <input
                type="file"
                id="photo-upload-natt"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                disabled={isUploadingPhoto}
                onClick={() => document.getElementById('photo-upload-natt')?.click()}
              >
                {isUploadingPhoto ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Laster opp...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Last opp nytt bilde
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Dette bildet brukes som profilbilde i "Hva skjedde i natt?" artiklene
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <div className="space-y-3 mb-3">
            <h3 className="text-sm font-medium">Manuell publisering (valgfritt)</h3>
            <div className="flex gap-2">
              <Input
                placeholder="dd.mm.yyyy"
                value={manualSearchDate}
                onChange={(e) => setManualSearchDate(e.target.value)}
                className="flex-1"
              />
              <Input
                placeholder="NTB ID (f.eks. 19417299)"
                value={manualNtbId}
                onChange={(e) => setManualNtbId(e.target.value)}
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              La feltene stå tomme for å bruke dagens "Nattens viktigste nyheter" artikkel, eller fyll inn for å velge en spesifikk NTB-artikkel
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="flex-1"
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publiserer...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Publiser nå
                </>
              )}
            </Button>
            <Button
              onClick={() => queryClient.invalidateQueries()}
              variant="outline"
              size="icon"
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => toggleMutation.mutate(taskStatus?.status !== 'active')}
              disabled={toggleMutation.isPending}
              variant={taskStatus?.status === 'paused' ? 'default' : 'secondary'}
              className="flex-1"
            >
              {toggleMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Endrer...
                </>
              ) : taskStatus?.status === 'paused' ? (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Aktiver automatisk publisering
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause automatisk publisering
                </>
              )}
            </Button>
            <Button
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending || !todayStatus?.published}
              variant="destructive"
              size="icon"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* History */}
        {publishHistory && publishHistory.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Siste publiseringer</h3>
            <div className="space-y-2">
              {publishHistory.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center justify-between p-2 rounded-lg border bg-card text-sm"
                >
                  <div>
                    <div className="font-medium">
                      {format(new Date(article.created_at), 'dd. MMMM yyyy', { locale: nb })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Kl. {format(new Date(article.created_at), 'HH:mm', { locale: nb })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">Publisert</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Logs */}
        {taskLogs && taskLogs.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Logg</h3>
            <div className="space-y-2">
              {taskLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-2 p-2 rounded-lg border bg-card text-sm"
                >
                  {log.status === 'success' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : log.status === 'failed' ? (
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{log.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: nb })}
                      {log.execution_time_ms && ` • ${log.execution_time_ms}ms`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
